import type { ImplementedLocale } from '~/lib/i18n/shared'
import { COMPLIANCE_POLICY_VERSION } from '@ballpeek/api/client'

export const legalDocumentIds = ['terms', 'privacy', 'disclaimer'] as const
export type LegalDocumentId = typeof legalDocumentIds[number]

interface LegalSection {
  id: string
  title: string
  paragraphs: string[]
  bullets?: string[]
}

export interface LegalDocumentContent {
  title: string
  summary: string
  effectiveLabel: string
  effectiveDate: string
  sections: LegalSection[]
}

const englishDocuments: Record<LegalDocumentId, LegalDocumentContent> = {
  terms: {
    title: 'Terms of Service',
    summary: 'These terms govern access to BallPeek as a monitoring and research information service.',
    effectiveLabel: 'Effective',
    effectiveDate: COMPLIANCE_POLICY_VERSION,
    sections: [
      {
        id: 'service',
        title: 'The service',
        paragraphs: [
          'BallPeek organizes football fixtures, prediction-market activity, reference odds, and historical research. It does not accept wagers, execute trades, hold customer funds, or act on a user’s behalf.',
          'Market observations, scores, and research are provided for information and independent decision support only.',
        ],
      },
      {
        id: 'eligibility',
        title: 'Eligibility and responsibility',
        paragraphs: [
          'You must be at least 18 years old and legally able to use this information service where you live. You are responsible for understanding and following the laws and platform rules that apply to you.',
        ],
      },
      {
        id: 'account',
        title: 'Your account',
        paragraphs: [
          'Provide accurate registration information, protect your credentials, and promptly report suspected unauthorized access. You may delete your local account from Settings. Dodo Payments may retain transaction records as Merchant of Record.',
        ],
      },
      {
        id: 'acceptable-use',
        title: 'Acceptable use',
        paragraphs: ['Do not misuse the service or interfere with other users, providers, or infrastructure.'],
        bullets: [
          'Do not attempt unauthorized access, automated abuse, or security testing without written permission.',
          'Do not present BallPeek information as a guaranteed result, betting recommendation, or proof of profit.',
          'Do not resell or republish third-party data in violation of its source terms.',
        ],
      },
      {
        id: 'billing',
        title: 'Paid access',
        paragraphs: [
          'Paid plans are processed by Dodo Payments as Merchant of Record. Prices, taxes, payment methods, cancellation, and applicable refund terms are shown in the hosted checkout and customer portal. A browser redirect is not proof of entitlement; verified billing events control paid access. An active or past-due Pro subscription must be canceled before the local account can be deleted.',
        ],
      },
      {
        id: 'availability',
        title: 'Availability and changes',
        paragraphs: [
          'We may change, suspend, or discontinue features when providers, security, law, or product requirements change. We may restrict accounts that violate these terms. Material policy changes will use a new effective version.',
        ],
      },
      {
        id: 'limits',
        title: 'No warranty of market outcomes',
        paragraphs: [
          'Data can be delayed, incomplete, unavailable, or incorrect. To the extent permitted by law, BallPeek is provided without a warranty that information will be uninterrupted, current, or suitable for a particular financial decision. Nothing in these terms limits rights that cannot legally be limited.',
        ],
      },
    ],
  },
  privacy: {
    title: 'Privacy Policy',
    summary: 'This policy explains which account data BallPeek handles, why it is used, and what deletion removes.',
    effectiveLabel: 'Effective',
    effectiveDate: COMPLIANCE_POLICY_VERSION,
    sections: [
      {
        id: 'data',
        title: 'Data we handle',
        paragraphs: ['We collect only the data needed to operate, secure, and improve the service.'],
        bullets: [
          'Account data such as name, email address, authentication records, and consent evidence.',
          'Billing identifiers and subscription status from Dodo Payments; BallPeek does not store full payment-card details.',
          'Telegram identifiers and alert preferences if you link Telegram in a future alerting flow.',
          'Product usage, paywall interactions, request identifiers, device/browser diagnostics, and security logs.',
        ],
      },
      {
        id: 'use',
        title: 'How data is used',
        paragraphs: [
          'We use account data to authenticate users, provide requested features, project paid entitlements, deliver opted-in communications, prevent abuse, diagnose failures, and understand whether product boundaries are useful.',
          'We do not sell personal data or use it to place wagers or execute trades.',
        ],
      },
      {
        id: 'providers',
        title: 'Service providers',
        paragraphs: [
          'Data may be processed by infrastructure and product providers needed to run the service, including Cloudflare, database hosting, Sentry, Dodo Payments, and Telegram when you choose to link it. Each provider handles data under its own terms and role.',
        ],
      },
      {
        id: 'retention',
        title: 'Retention and account deletion',
        paragraphs: [
          'Deleting your account removes the local user, sessions, linked accounts, profile, registration-consent record, local billing projection, and linked communication data that exists at deletion time. Retained operator records are anonymized where they contain your user identifier or email.',
          'Dodo Payments transaction records remain with Dodo as Merchant of Record where required for tax, payment, dispute, fraud, or legal obligations, but account deletion is blocked until a renewable Pro subscription is canceled. Limited logs and backups may remain temporarily until their normal expiry cycle.',
        ],
      },
      {
        id: 'security',
        title: 'Security',
        paragraphs: [
          'We use authenticated sessions, signed tokens, access controls, encrypted transport, and operational logging. No system is risk-free; protect your credentials and report unexpected account activity promptly.',
        ],
      },
      {
        id: 'choices',
        title: 'Your choices',
        paragraphs: [
          'You can review account settings, manage paid access through the hosted customer portal, unlink optional communication channels when available, and permanently delete your local account. Registration is optional, but authenticated features require the account data described here.',
        ],
      },
      {
        id: 'changes',
        title: 'Policy changes',
        paragraphs: [
          'When this policy materially changes, the effective version changes. New registrations must accept the current version; existing-user re-consent may be required when a change affects how personal data is handled.',
        ],
      },
    ],
  },
  disclaimer: {
    title: 'Disclaimer',
    summary: 'BallPeek provides monitoring information—not betting, investment, financial, tax, or legal advice.',
    effectiveLabel: 'Effective',
    effectiveDate: COMPLIANCE_POLICY_VERSION,
    sections: [
      {
        id: 'no-advice',
        title: 'Information, not advice',
        paragraphs: [
          'Scores, price differences, whale activity, reference odds, historical statistics, and generated summaries help users inspect market conditions. They are not instructions to bet, trade, buy, sell, or size a position.',
        ],
      },
      {
        id: 'no-guarantee',
        title: 'No guaranteed edge or outcome',
        paragraphs: [
          'A Market Dislocation is an observed pricing difference, not proof of risk-free arbitrage, insider knowledge, expected profit, or a “sure win.” Decision-Support Scores prioritize evidence; they do not predict results or recommend action.',
        ],
      },
      {
        id: 'data-limits',
        title: 'Data limitations',
        paragraphs: [
          'Third-party prices, fixtures, lineups, injuries, statistics, and market status can change without notice. Sources can disagree or become stale. Always inspect source, scope, mapping, and freshness information before relying on a comparison.',
        ],
      },
      {
        id: 'risk',
        title: 'You bear the risk',
        paragraphs: [
          'Prediction markets and sports wagering can result in total loss and may be restricted where you live. You are solely responsible for your decisions, eligibility, taxes, platform accounts, and compliance with applicable law.',
        ],
      },
      {
        id: 'third-parties',
        title: 'Third-party services',
        paragraphs: [
          'Links and references to Polymarket, bookmakers, data providers, Dodo Payments, or Telegram do not constitute an endorsement or control of those services. Their availability, rules, and data practices are governed by their own terms.',
        ],
      },
      {
        id: 'generated-content',
        title: 'Generated and summarized content',
        paragraphs: [
          'Automated or AI-assisted summaries are covered by this same disclaimer. They may omit context or contain errors and must be checked against the displayed source evidence.',
        ],
      },
    ],
  },
}

const simplifiedChineseDocuments: Record<LegalDocumentId, LegalDocumentContent> = {
  terms: {
    title: '服务条款',
    summary: '本条款适用于将 BallPeek 作为市场监测与研究信息服务使用的情形。',
    effectiveLabel: '生效版本',
    effectiveDate: COMPLIANCE_POLICY_VERSION,
    sections: [
      { id: 'service', title: '服务内容', paragraphs: ['BallPeek 整理足球赛程、预测市场活动、参考赔率和历史研究资料。平台不接受投注、不执行交易、不托管用户资金，也不会代用户采取行动。', '市场观察、评分和研究资料仅用于信息展示与独立判断支持。'] },
      { id: 'eligibility', title: '资格与责任', paragraphs: ['您必须年满 18 周岁，并且在所在地区依法可以使用此类信息服务。您有责任了解并遵守适用于自己的法律和第三方平台规则。'] },
      { id: 'account', title: '您的账户', paragraphs: ['请提供准确的注册资料，妥善保护登录凭据，并及时处理可疑访问。您可以在“账户设置”中删除本地账户。Dodo Payments 作为记录商户，仍可能依法保留交易记录。'] },
      { id: 'acceptable-use', title: '允许的使用方式', paragraphs: ['不得滥用服务，也不得干扰其他用户、数据提供商或基础设施。'], bullets: ['不得在未经书面许可的情况下尝试未授权访问、自动化滥用或安全测试。', '不得把 BallPeek 信息宣传为稳赚结果、投注建议或盈利证明。', '不得违反数据源条款转售或重新发布第三方数据。'] },
      { id: 'billing', title: '付费访问', paragraphs: ['付费计划由 Dodo Payments 作为记录商户处理。价格、税费、支付方式、取消及适用退款条件以托管结账页和客户门户显示为准。浏览器跳转不代表已获得付费权限；经验证的账单事件才是权限依据。仍在续费或欠费宽限期内的 Pro 订阅必须先取消，之后才能删除本地账户。'] },
      { id: 'availability', title: '可用性与变更', paragraphs: ['当数据源、安全、法律或产品要求变化时，我们可能调整、暂停或停止部分功能；违反条款的账户可能被限制。重大政策变化将使用新的生效版本。'] },
      { id: 'limits', title: '不保证市场结果', paragraphs: ['数据可能延迟、不完整、不可用或错误。在法律允许范围内，BallPeek 不保证信息持续可用、始终最新或适合特定财务决定。本条款不限制依法不能被限制的权利。'] },
    ],
  },
  privacy: {
    title: '隐私政策',
    summary: '本政策说明 BallPeek 处理哪些账户数据、处理目的，以及删除账户会移除什么。',
    effectiveLabel: '生效版本',
    effectiveDate: COMPLIANCE_POLICY_VERSION,
    sections: [
      { id: 'data', title: '处理的数据', paragraphs: ['我们只收集运行、保护和改进服务所需的数据。'], bullets: ['姓名、邮箱、认证记录和注册同意证据。', '来自 Dodo Payments 的账单标识和订阅状态；BallPeek 不保存完整银行卡资料。', '如果将来主动绑定 Telegram，则包括 Telegram 标识和告警偏好。', '产品使用、付费墙交互、请求标识、设备/浏览器诊断和安全日志。'] },
      { id: 'use', title: '使用目的', paragraphs: ['我们使用账户数据进行身份认证、提供请求的功能、投影付费权限、发送用户主动订阅的通信、防止滥用、诊断故障，并判断产品边界是否有效。', '我们不出售个人数据，也不会使用个人数据代用户投注或交易。'] },
      { id: 'providers', title: '服务提供商', paragraphs: ['运行服务所需的数据可能由 Cloudflare、数据库托管服务、Sentry、Dodo Payments，以及用户主动绑定时的 Telegram 处理。各提供商依照自身条款和角色处理数据。'] },
      { id: 'retention', title: '保留与账户删除', paragraphs: ['删除账户会移除本地用户、会话、关联登录账户、用户资料、注册同意记录、本地账单权限投影，以及删除时已经存在的通信绑定数据。保留的运营审计记录如包含用户标识或邮箱，将进行匿名化。', 'Dodo Payments 作为记录商户，可能因税务、支付、争议、反欺诈或法律义务继续保存交易记录，但仍可续费的 Pro 订阅必须先取消，账户才能删除。少量日志和备份可能在正常到期周期结束前暂时保留。'] },
      { id: 'security', title: '安全措施', paragraphs: ['我们使用认证会话、签名令牌、访问控制、加密传输和运营日志。任何系统都无法完全消除风险；请保护登录凭据并及时处理异常账户活动。'] },
      { id: 'choices', title: '您的选择', paragraphs: ['您可以查看账户设置，通过托管客户门户管理付费访问，在功能提供后解绑可选通信渠道，并永久删除本地账户。注册是可选的，但需要登录的功能必须处理本政策所述账户数据。'] },
      { id: 'changes', title: '政策变更', paragraphs: ['本政策发生重大变化时，生效版本将更新。新注册用户必须接受当前版本；如果变更影响个人数据处理方式，现有用户也可能需要重新同意。'] },
    ],
  },
  disclaimer: {
    title: '免责声明',
    summary: 'BallPeek 提供市场监测信息，不构成投注、投资、财务、税务或法律建议。',
    effectiveLabel: '生效版本',
    effectiveDate: COMPLIANCE_POLICY_VERSION,
    sections: [
      { id: 'no-advice', title: '信息不是建议', paragraphs: ['评分、价格差异、大额交易活动、参考赔率、历史统计和自动摘要用于帮助查看市场状况，不是要求用户投注、交易、买入、卖出或决定仓位大小的指令。'] },
      { id: 'no-guarantee', title: '不保证优势或结果', paragraphs: ['“市场错位”表示观察到的价格差异，不代表无风险套利、内幕信息、预期盈利或“稳赚”。“决策支持评分”只用于排列证据，不预测结果，也不建议行动。'] },
      { id: 'data-limits', title: '数据限制', paragraphs: ['第三方价格、赛程、阵容、伤病、统计和市场状态可能随时变化；不同来源可能冲突或过期。依赖比较结果前，请检查页面显示的数据源、范围、映射关系和更新时间。'] },
      { id: 'risk', title: '风险由用户承担', paragraphs: ['预测市场和体育博彩可能造成全部损失，在部分地区也可能受限。用户须自行对决定、资格、税务、第三方平台账户及法律合规负责。'] },
      { id: 'third-parties', title: '第三方服务', paragraphs: ['页面提及或链接 Polymarket、博彩公司、数据提供商、Dodo Payments 或 Telegram，不代表 BallPeek 为其背书或能够控制其服务。相关可用性、规则和数据处理以第三方条款为准。'] },
      { id: 'generated-content', title: '自动生成与摘要内容', paragraphs: ['自动化或 AI 辅助摘要同样适用本免责声明。摘要可能遗漏上下文或包含错误，必须结合页面展示的来源证据核对。'] },
    ],
  },
}

export function isLegalDocumentId(value: string): value is LegalDocumentId {
  return legalDocumentIds.includes(value as LegalDocumentId)
}

export function getLegalDocumentContent(
  locale: ImplementedLocale,
  document: LegalDocumentId,
): LegalDocumentContent {
  return locale === 'zh-CN'
    ? simplifiedChineseDocuments[document]
    : englishDocuments[document]
}
